import * as React from 'react';
import { Body } from 'vet-bones/design-system/components';

import {
  DataSourceType,
  SAMPLE_MUSIC_DATA_SOURCE_TYPE,
} from 'src/constants/dataSourceTypeDescription';
import { DataSourceTypeCard } from 'src/containers/ProjectVirtualGraphPreviewDialog/components/DataSourceTypeCard';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import * as copy from 'src/templates/copy';
import { useDesignerPermissions } from 'src/utils/hooks/useDesignerPermissions';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useExistingSampleMusicDataSource } from 'src/utils/hooks/useSampleMusicDataSource';

import styles from 'src/components/dataSourceSelection/dataSourceTypeGrid.module.scss';

export type DataSourceTypeGridProps = {
  /** List of data source types to display */
  dataSourceTypes: DataSourceType[];
  /** Callback when a data source type card is clicked */
  onSelectType: (dataSourceType: DataSourceType) => void;
  /** Whether the sample data source is loading */
  isSampleLoading?: boolean;
};

/**
 * A grid of data source type cards with demo types separated by a divider.
 * Used by both the "add resource" and "replace data source" flows.
 */
export const DataSourceTypeGrid: React.VFC<DataSourceTypeGridProps> = ({
  dataSourceTypes,
  onSelectType,
  isSampleLoading = false,
}) => {
  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);
  const { canCreateDataSource, canReadDataSource } = useDesignerPermissions();
  const existingSampleMusicDataSource = useExistingSampleMusicDataSource();

  const disabledTooltip = (dataSourceType: string) => {
    const hasExisting =
      dataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE
        ? Boolean(existingSampleMusicDataSource)
        : Boolean((dataSourcesByType[dataSourceType] || []).length);

    if (hasExisting) {
      if (!canCreateDataSource && !canReadDataSource) {
        return copy.components.dialogConnectionTile
          .insufficientPrivilegesCreateOrReadDataSource;
      }
    } else if (!canCreateDataSource) {
      return copy.components.dialogConnectionTile
        .insufficientPrivilegesCreateDataSource;
    }

    return undefined;
  };

  // Separate regular data source types from demo types
  const regularTypes = dataSourceTypes.filter(
    (type) => type !== SAMPLE_MUSIC_DATA_SOURCE_TYPE
  );
  const hasDemoType = dataSourceTypes.includes(SAMPLE_MUSIC_DATA_SOURCE_TYPE);

  return (
    <div className={styles.dataSourceTypeGrid}>
      {regularTypes.map((dataSourceType) => (
        <DataSourceTypeCard
          key={dataSourceType}
          dataSourceType={dataSourceType}
          disabledTooltip={disabledTooltip(dataSourceType)}
          onClick={() => onSelectType(dataSourceType)}
        />
      ))}

      {/* Demo section divider */}
      {hasDemoType ? (
        <>
          <div className={styles.gridDivider}>
            <div className={styles.gridDividerLine} />
            <Body size="sm" weight="regular">
              {
                copy.components.virtualGraphPreviewDialog.connectionSelection
                  .sample
              }
            </Body>
            <div className={styles.gridDividerLine} />
          </div>
          <DataSourceTypeCard
            dataSourceType={SAMPLE_MUSIC_DATA_SOURCE_TYPE}
            disabledTooltip={disabledTooltip(SAMPLE_MUSIC_DATA_SOURCE_TYPE)}
            isLoading={isSampleLoading}
            onClick={() => onSelectType(SAMPLE_MUSIC_DATA_SOURCE_TYPE)}
          />
        </>
      ) : null}
    </div>
  );
};
